import axios, { AxiosResponse } from 'axios'

import { ListResponse, RawReservationCreateResponse } from '@/api/manual_types'

import {
  PerformanceId,
  ReservationCreate,
  ReservationDetailedRead,
  ReservationGuestModify,
  ReservationId,
  ReservationPaidUpdate,
  ReservationRead,
  ReservationUpdate,
  StripeReaderId,
  SuccessResponse,
  TransactionRead,
} from '@generated/types'

const BASE_URL = '/api/reservation'
const TRANSACTION_BASE_URL = '/api/transaction' // Base URL for transaction-related endpoints

export default {
  async fetchAll(params?: {
    active: boolean
  }): Promise<AxiosResponse<ListResponse<ReservationRead>>> {
    const queryString = Object.entries(params || {})
      .map(entry => entry[0] + '=' + entry[1])
      .join('&')
    return axios.get(`${BASE_URL}/?${queryString}`)
  },

  async fetchOne(id: ReservationId): Promise<AxiosResponse<ReservationDetailedRead>> {
    return axios.get(`${BASE_URL}/${id}/`)
  },

  async fetchByPerformance(
    performanceId: PerformanceId,
  ): Promise<AxiosResponse<ReservationDetailedRead[]>> {
    return axios.get(`${BASE_URL}/by-performance/?performance_id=${performanceId}`)
  },

  async reserve(data: ReservationCreate): Promise<AxiosResponse<RawReservationCreateResponse>> {
    return axios.post(`${BASE_URL}/reserve/`, data)
  },

  async create(data: ReservationCreate): Promise<AxiosResponse<RawReservationCreateResponse>> {
    return axios.post(`${BASE_URL}/`, data)
  },

  async retry(id: ReservationId, reader_id?: StripeReaderId): Promise<AxiosResponse> {
    const query = reader_id ? `?reader_id=${reader_id}` : ''
    return axios.post(`${BASE_URL}/${id}/retry/${query}`)
  },

  async update(data: ReservationUpdate): Promise<AxiosResponse<ReservationRead>> {
    // Ensure data.id exists before using it in the URL
    if (data.id === undefined || data.id === null) {
      return Promise.reject(new Error('Reservation ID is required for update.'))
    }
    return axios.put(`${BASE_URL}/${data.id}/`, data)
  },

  async updatePaid(data: ReservationPaidUpdate): Promise<AxiosResponse<ReservationRead>> {
    // Ensure data.id exists before using it in the URL
    if (data.id === undefined || data.id === null) {
      return Promise.reject(new Error('Reservation ID is required for paid update.'))
    }
    return axios.post(`${BASE_URL}/${data.id}/paid/`, data)
  },

  async pay(id: ReservationId, reader_id: StripeReaderId): Promise<AxiosResponse<SuccessResponse>> {
    return axios.post(`${BASE_URL}/${id}/pay/`, { reader_id })
  },

  // Note: Moved refundTransaction here as it's closely related to reservations/payments
  async refundTransaction(data: {
    transactionId: number
    amount: number
  }): Promise<AxiosResponse<TransactionRead>> {
    return axios.post(`${TRANSACTION_BASE_URL}/${data.transactionId}/refund/`, {
      amount: data.amount,
    })
  },

  async abandon(
    id: ReservationId,
    data?: Pick<ReservationGuestModify, 'reservation_key'>,
  ): Promise<AxiosResponse> {
    return axios.post(`${BASE_URL}/${id}/abandon/`, data)
  },

  async resendConfirmation(
    id: ReservationId, // Changed type from number
    email: string,
  ): Promise<AxiosResponse> {
    return axios.post(`${BASE_URL}/${id}/resend-confirmation/`, { email })
  },

  async delete(id: ReservationId): Promise<AxiosResponse> {
    // Changed type from number
    return axios.delete(`${BASE_URL}/${id}/`)
  },

  async fetchStatus(id: ReservationId): Promise<AxiosResponse<ReservationRead>> {
    return axios.get(`${BASE_URL}/${id}/status/`)
  },

  async guestModify(
    reservationId: ReservationId,
    guest: ReservationGuestModify,
  ): Promise<AxiosResponse<ReservationDetailedRead>> {
    return axios.post(`${BASE_URL}/${reservationId}/modify/`, guest)
  },
}
